import { zodResolver } from '@hookform/resolvers/zod';
import { format } from 'date-fns';
import { de } from 'date-fns/locale/de';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { CreateProjectHeader } from '@/features/projects/create-project/components/layouts/create-project-header';
import { createProjectFormSchema } from '@/features/projects/create-project/schema/create-project-form-schema';
import {
  CreateProjectStoreSetData,
  useCreateProjectStore,
} from '@/features/projects/create-project/store/use-create-project-store';
import CalendarIcon from '@/shared/assets/calendar.svg?react';
import { FlowFormFields } from '@/shared/components/flow/flow-fields';
import { Button } from '@/shared/components/ui/button';
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/shared/components/ui/card';
import { CustomDatePickerDialog } from '@/shared/components/ui/custom-date-picker-dialog';
import {
  Form,
  FormControl,
  FormField,
  FormLabel,
  FormItem,
  FormMessage,
} from '@/shared/components/ui/form';
import { LabelInput } from '@/shared/components/ui/label-input';
import { LabelTextarea } from '@/shared/components/ui/label-textarea';

interface CreateProjectInfoFormProps {
  onSuccess: () => void;
  onChange: (data: CreateProjectStoreSetData) => void;
}

export const CreateProjectInfoForm = ({
  onSuccess,
  onChange,
}: CreateProjectInfoFormProps) => {
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const { t: commonT } = useTranslation('common');
  const { t } = useTranslation('project', {
    keyPrefix: 'ProjectInfoForm',
  });
  const { t: sharedT } = useTranslation('project', { keyPrefix: 'Shared' });
  const formSchema = createProjectFormSchema({
    projectNameError: t('project_name_error'),
    endDateError: commonT('form_field.date.end_date_error'),
  });

  const {
    data: { projectName, projectDescription, date },
  } = useCreateProjectStore();

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      projectName: projectName || '',
      projectDescription: projectDescription || '',
      startDate: date?.from || undefined,
      endDate: date?.to || undefined,
    },
  });

  function onSubmit(values: z.infer<typeof formSchema>) {
    onChange({
      projectName: values.projectName,
      projectDescription: values.projectDescription,
      date:
        values.startDate && values.endDate
          ? {
              startDate: values.startDate,
              endDate: values.endDate,
            }
          : undefined,
    });
    onSuccess();
  }

  const formStartDate = form.watch('startDate');
  const formEndDate = form.watch('endDate');

  return (
    <>
      <CreateProjectHeader />
      <Card variant="form" wrapWithStars>
        <CardHeader>
          <CardTitle>{t('title')}</CardTitle>
        </CardHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <CardContent>
              <FlowFormFields>
                <FormField
                  control={form.control}
                  name="projectName"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <LabelInput
                          label={t('project_name_label')}
                          placeholder={t('project_name_placeholder')}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="projectDescription"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <LabelTextarea
                          label={
                            t('project_description_label') +
                            ' ' +
                            commonT('hint.optional')
                          }
                          placeholder={t('project_description_placeholder')}
                          resize={true}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <div className="flex flex-col gap-2">
                  <FormLabel>
                    {t('start_date_label')} / {t('end_date_label')}{' '}
                    <span className="text-white/50">
                      {commonT('hint.optional')}
                    </span>
                  </FormLabel>
                  <CustomDatePickerDialog
                    isOpen={isDatePickerOpen}
                    onOpenChange={setIsDatePickerOpen}
                    startDate={formStartDate}
                    endDate={formEndDate}
                    onDateRangeSelect={(start, end) => {
                      form.setValue('startDate', start);
                      form.setValue('endDate', end);
                    }}
                    title={sharedT('select_time_period')}
                  >
                    <div className="flex cursor-pointer flex-row items-center gap-2">
                      <div className="flex h-[46px] flex-1 items-center justify-between gap-2 rounded-xs border border-white/20 bg-transparent px-4">
                        <span
                          className={
                            formStartDate
                              ? 'text-xs text-white'
                              : 'text-xs text-white/60'
                          }
                        >
                          {formStartDate
                            ? format(formStartDate, 'dd.MM.yyyy', {
                                locale: de,
                              })
                            : t('start_date_label')}
                        </span>
                        <CalendarIcon className="size-4 flex-shrink-0 text-white/60" />
                      </div>
                      <span className="text-white/60">-</span>
                      <div className="flex h-[46px] flex-1 items-center justify-between gap-2 rounded-xs border border-white/20 bg-transparent px-4">
                        <span
                          className={
                            formEndDate
                              ? 'text-xs text-white'
                              : 'text-xs text-white/60'
                          }
                        >
                          {formEndDate
                            ? format(formEndDate, 'dd.MM.yyyy', { locale: de })
                            : t('end_date_label')}
                        </span>
                        <CalendarIcon className="size-4 flex-shrink-0 text-white/60" />
                      </div>
                    </div>
                  </CustomDatePickerDialog>
                </div>
              </FlowFormFields>
            </CardContent>
            <CardFooter>
              <Button
                className="w-40"
                type="submit"
                variant={'greenGradient'}
                btnType="primary"
              >
                {t('submit_button_label')}
              </Button>
            </CardFooter>
          </form>
        </Form>
      </Card>
    </>
  );
};
